import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Badge,
  Chip,
  InputAdornment,
  ThemeProvider,
  createTheme
} from '@mui/material';
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  WhatsApp as WhatsAppIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import adminAuthService from '../services/adminAuthService';
import { API_BASE_URL } from '../config/api';

const WhatsAppMessages = () => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef(null);
  const autoRefreshInterval = useRef(null);

  // RTL theme
  const rtlTheme = createTheme({
    direction: 'rtl',
    typography: {
      fontFamily: '"Cairo", "Arial", sans-serif'
    }
  });

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversations
  const loadConversations = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/whatsapp_messages.php?action=get_conversations`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setConversations(data.conversations || []);
      } else {
        setError(data.message || 'فشل في تحميل المحادثات');
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
      setError('حدث خطأ في تحميل المحادثات');
    } finally {
      setLoading(false);
    }
  };

  // Load messages for a conversation
  const loadMessages = async (phoneNumber) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/whatsapp_messages.php?action=get_messages&phone=${encodeURIComponent(phoneNumber)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${adminAuthService.getToken()}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();
      
      if (data.success) {
        setMessages(data.messages || []);
        // Mark messages as read
        await markMessagesAsRead(phoneNumber);
      } else {
        setError(data.message || 'فشل في تحميل الرسائل');
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      setError('حدث خطأ في تحميل الرسائل');
    }
  };

  // Mark messages as read
  const markMessagesAsRead = async (phoneNumber) => {
    try {
      await fetch(`${API_BASE_URL}/api/whatsapp_messages.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'mark_read',
          phone: phoneNumber
        })
      });
      
      // Update conversation unread count
      setConversations(prev => 
        prev.map(conv => 
          conv.phone === phoneNumber 
            ? { ...conv, unread_count: 0 }
            : conv
        )
      );
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  // Send message
  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedConversation) return;

    setSending(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/whatsapp_messages.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'send_message',
          phone: selectedConversation.phone,
          message: messageText
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Add message to local state immediately
        const newMessage = {
          id: Date.now(),
          message: messageText,
          direction: 'outgoing',
          timestamp: new Date().toISOString(),
          status: 'sent'
        };
        
        setMessages(prev => [...prev, newMessage]);
        setMessageText('');
        setSuccess('تم إرسال الرسالة');
        setTimeout(() => setSuccess(''), 3000);
        
        // Reload conversations to update last message
        loadConversations();
      } else {
        setError(data.message || 'فشل في إرسال الرسالة');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setError('حدث خطأ في إرسال الرسالة');
    } finally {
      setSending(false);
    }
  };

  // Handle conversation selection
  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    loadMessages(conversation.phone);
  };

  // Auto-refresh messages
  useEffect(() => {
    loadConversations();

    // Set up auto-refresh every 10 seconds
    autoRefreshInterval.current = setInterval(() => {
      loadConversations();
      if (selectedConversation) {
        loadMessages(selectedConversation.phone);
      }
    }, 10000);

    return () => {
      if (autoRefreshInterval.current) {
        clearInterval(autoRefreshInterval.current);
      }
    };
  }, [selectedConversation]);

  // Filter conversations by search
  const filteredConversations = conversations.filter(conv => 
    conv.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conv.phone?.includes(searchQuery)
  );

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const hours = diff / (1000 * 60 * 60);

    if (hours < 24) {
      return date.toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('ar-EG', { month: 'short', day: 'numeric' });
    }
  };

  return (
    <ThemeProvider theme={rtlTheme}>
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
        {/* Header */}
        <Paper 
          elevation={2} 
          sx={{ 
            p: 2, 
            borderRadius: 0,
            background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <WhatsAppIcon sx={{ fontSize: 40, color: 'white' }} />
              <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
                رسائل واتساب
              </Typography>
            </Box>
            <IconButton 
              onClick={() => {
                loadConversations();
                if (selectedConversation) loadMessages(selectedConversation.phone);
              }}
              sx={{ color: 'white' }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
        </Paper>

        {/* Alerts */}
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={() => setSuccess('')} sx={{ m: 2 }}>
            {success}
          </Alert>
        )}

        {/* Main Content */}
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Conversations List */}
          <Paper 
            elevation={3}
            sx={{ 
              width: 320, 
              display: 'flex', 
              flexDirection: 'column',
              borderRadius: 0,
              borderRight: '1px solid #e0e0e0'
            }}
          >
            {/* Search */}
            <Box sx={{ p: 2, bgcolor: '#f8f9fa' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="ابحث عن محادثة..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>

            {/* Conversations */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : filteredConversations.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="textSecondary">
                    لا توجد محادثات
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {filteredConversations.map((conversation) => (
                    <React.Fragment key={conversation.phone}>
                      <ListItem
                        button
                        selected={selectedConversation?.phone === conversation.phone}
                        onClick={() => handleSelectConversation(conversation)}
                        sx={{
                          '&.Mui-selected': {
                            bgcolor: '#e8f5e9'
                          },
                          '&:hover': {
                            bgcolor: '#f5f5f5'
                          }
                        }}
                      >
                        <Avatar sx={{ bgcolor: '#25D366', ml: 2 }}>
                          <PersonIcon />
                        </Avatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {conversation.customer_name || conversation.phone}
                              </Typography>
                              {conversation.unread_count > 0 && (
                                <Badge badgeContent={conversation.unread_count} color="error" />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: 'text.secondary',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {conversation.last_message || 'لا توجد رسائل'}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {formatTimestamp(conversation.last_message_time)}
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Box>
          </Paper>

          {/* Chat Area */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#e5ddd5' }}>
            {selectedConversation ? (
              <>
                {/* Chat Header */}
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 2, 
                    borderRadius: 0,
                    bgcolor: '#f0f0f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}
                >
                  <Avatar sx={{ bgcolor: '#25D366' }}>
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {selectedConversation.customer_name || 'عميل'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {selectedConversation.phone}
                    </Typography>
                  </Box>
                </Paper>

                {/* Messages */}
                <Box 
                  sx={{ 
                    flex: 1, 
                    overflow: 'auto', 
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                  }}
                >
                  {messages.map((message) => (
                    <Box
                      key={message.id}
                      sx={{
                        display: 'flex',
                        justifyContent: message.direction === 'outgoing' ? 'flex-end' : 'flex-start'
                      }}
                    >
                      <Paper
                        elevation={1}
                        sx={{
                          p: 1.5,
                          maxWidth: '70%',
                          bgcolor: message.direction === 'outgoing' ? '#dcf8c6' : 'white',
                          borderRadius: 2
                        }}
                      >
                        <Typography variant="body1">
                          {message.message}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            display: 'block', 
                            textAlign: 'left',
                            color: 'text.secondary',
                            mt: 0.5
                          }}
                        >
                          {formatTimestamp(message.timestamp)}
                        </Typography>
                      </Paper>
                    </Box>
                  ))}
                  <div ref={messagesEndRef} />
                </Box>

                {/* Message Input */}
                <Paper 
                  elevation={3} 
                  sx={{ 
                    p: 2, 
                    borderRadius: 0,
                    bgcolor: '#f0f0f0'
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      placeholder="اكتب رسالتك..."
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      disabled={sending}
                      sx={{
                        bgcolor: 'white',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3
                        }
                      }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSendMessage}
                      disabled={!messageText.trim() || sending}
                      sx={{
                        minWidth: 56,
                        height: 56,
                        borderRadius: 3,
                        bgcolor: '#25D366',
                        '&:hover': {
                          bgcolor: '#128C7E'
                        }
                      }}
                    >
                      {sending ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
                    </Button>
                  </Box>
                </Paper>
              </>
            ) : (
              <Box 
                sx={{ 
                  flex: 1, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexDirection: 'column',
                  gap: 2
                }}
              >
                <WhatsAppIcon sx={{ fontSize: 80, color: '#25D366', opacity: 0.3 }} />
                <Typography variant="h6" color="textSecondary">
                  اختر محادثة لبدء المراسلة
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default WhatsAppMessages;
